import { AnimatePresence } from "framer-motion";
import { FC, useState } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";

import AddButton from "@/components/AddButton";
import { SrOnly } from "@/components/Layout/Parts";
import { FullWidthRow, Table, TableContent, Th } from "@/components/Table";
import TestingAudienceForm from "./TestingAudienceForm";
import { TestingAudienceResource } from "@/types/resources";
import { api } from "@/utils/api";
import TestingAudienceRow from "./TestingAudienceRow";
import ToggableCardHeading, { Box } from "./ToggableCardHeading";

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px 0 0;
`;

const StyledTable = styled(Table)`
  th,
  td {
    &:first-child {
      padding-left: 24px;
      width: 300px;
    }
    &:nth-child(2) {
      width: 170px;
    }
  }
  th {
    padding-bottom: 30px;
    padding-top: 24px;
  }
  td {
    padding-top: 22px;
    padding-bottom: 22px;
  }
`;

const TestingAudiences: FC = () => {
  const [showCreationForm, setShowCreationForm] = useState(false);

  const [showContent, setShowContent] = useState(false);

  const {
    data: audiences,
    isLoading,
    isError,
  } = useQuery(
    ["testing_audiences"],
    () => api.get<never, TestingAudienceResource[]>("/v1/testing-audiences"),
    {
      staleTime: 120 * 1000,
    }
  );

  return (
    <Box>
      <ToggableCardHeading
        setShowModal={setShowCreationForm}
        setShowContent={setShowContent}
        count={audiences?.length}
        showContent={showContent}
        title="Youtube optimisation testing audiences"
        AddbuttonText="Add audience"
      />
      {!!showContent && (
        <Card>
          <StyledTable $fixed>
            <thead>
              <tr>
                <Th>Audience name</Th>
                <Th>Channels</Th>
                <Th>Notes</Th>
                <Th $actionsCol>
                  <SrOnly>Actions</SrOnly>
                </Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                colSpan={4}
                isLoading={isLoading}
                isError={isError}
                hasData={!!audiences?.length}
              >
                {audiences?.map((audience) => (
                  <TestingAudienceRow key={audience.id} audience={audience} />
                ))}
              </TableContent>
              <FullWidthRow colSpan={4}>
                <AddButton onClick={() => setShowCreationForm(true)}>
                  Add audience
                </AddButton>
              </FullWidthRow>
            </tbody>
          </StyledTable>
        </Card>
      )}
      <AnimatePresence>
        {showCreationForm && (
          <TestingAudienceForm onClose={() => setShowCreationForm(false)} />
        )}
      </AnimatePresence>
    </Box>
  );
};

export default TestingAudiences;
